import { ChallengeTargetOperatorEnum } from '../../../common/enum/challenge-target/challenge-target-operator.enum';
import { LogManager } from '../../../common/port/log/log.manager.port';
import { AccountMetricInsightsMetricType } from '../../dtos/challenge-insights/account-metrics-insights-metric-type.enum';
import { GetChallengeInsightProgressSummaryRequestDto } from '../../dtos/challenge-insights/get-challenge-insight-progress-summary.request.dto';
import { GetChallengeInsightProgressSummaryResponseDto } from '../../dtos/challenge-insights/get-challenge-insight-progress-summary.response.dto';
import {
  GetChallengeInsightProgressSummaryPresenter,
  GetChallengeInsightProgressSummaryPresenterDomainEntities,
} from '../../presenters/challenge-insights/get-challenge-insight-progress-summary.presenter';
import { GetChallengeInsightProgressChallengeTargetSuccessQuery } from '../../queries/challenge-insights/get-challenge-insight-progress-challenge-target-success.query';
import { GetChallengeInsightProgressDigitalIntensitySummaryQuery } from '../../queries/challenge-insights/get-challenge-insight-progress-digtial-intensity-summary.query';
import { GetChallengeInsightProgressDigitalWorkingHoursSummaryQuery } from '../../queries/challenge-insights/get-challenge-insight-progress-digtial-working-hours-summary.query';
import { GetChallengeInsightProgressFocusV1SummaryQuery } from '../../queries/challenge-insights/get-challenge-insight-progress-focus-v1-summary.query';
import { GetChallengeInsightProgressSentimentSummaryQuery } from '../../queries/challenge-insights/get-challenge-insight-progress-sentiment-summary.query';
import { GetChallengeInsightProgressTimeInAppSummaryQuery } from '../../queries/challenge-insights/get-challenge-insight-progress-time-in-app-summary.query';
import { GetChallengeInsightProgressTimeInCallsSummaryQuery } from '../../queries/challenge-insights/get-challenge-insight-progress-time-in-calls-summary.query';
import { GetChallengeInsightProgressTimeInCategorySummaryQuery } from '../../queries/challenge-insights/get-challenge-insight-progress-time-in-category-summary.query';
import { GetChallengeInsightProgressTimeInMeetingsSummaryQuery } from '../../queries/challenge-insights/get-challenge-insight-progress-time-in-meetings-summary.query';
import { GetChallengeInsightProgressSelfReportSummaryQuery } from '../../queries/challenge-insights/get-challenge-insight-self-report-summary.query';

export class GetChallengeInsightProgressSummaryCompositeController {
  constructor(
    protected readonly logManager: LogManager,
    protected readonly getChallengeInsightProgressChallengeTargetSuccessQuery: GetChallengeInsightProgressChallengeTargetSuccessQuery,
    protected readonly getChallengeInsightProgressTimeInCategorySummaryQuery: GetChallengeInsightProgressTimeInCategorySummaryQuery,
    protected readonly getChallengeInsightProgressTimeInAppSummaryQuery: GetChallengeInsightProgressTimeInAppSummaryQuery,
    protected readonly getChallengeInsightProgressDigitalWorkingHoursSummaryQuery: GetChallengeInsightProgressDigitalWorkingHoursSummaryQuery,
    protected readonly getChallengeInsightProgressTimeInCallsSummaryQuery: GetChallengeInsightProgressTimeInCallsSummaryQuery,
    protected readonly getChallengeInsightProgressFocusV1SummaryQuery: GetChallengeInsightProgressFocusV1SummaryQuery,
    protected readonly getChallengeInsightProgressSentimentSummaryQuery: GetChallengeInsightProgressSentimentSummaryQuery,
    protected readonly getChallengeInsightProgressTimeInMeetingsSummaryQuery: GetChallengeInsightProgressTimeInMeetingsSummaryQuery,
    protected readonly getChallengeInsightProgressDigitalIntensitySummaryQuery: GetChallengeInsightProgressDigitalIntensitySummaryQuery,
    protected readonly getChallengeInsightProgressSelfReportSummaryQuery: GetChallengeInsightProgressSelfReportSummaryQuery,
    protected readonly presenter: GetChallengeInsightProgressSummaryPresenter
  ) {}
  async get(
    props: GetChallengeInsightProgressSummaryRequestDto
  ): Promise<GetChallengeInsightProgressSummaryResponseDto> {
    this.logManager.debug(
      'GetChallengeInsightProgressSummaryCompositeController has been called with props:',
      props
    );

    const metricType = props.params.metricType;
    const promises: Partial<
      [
        Promise<number>,
        Promise<GetChallengeInsightProgressSummaryPresenterDomainEntities>,
      ]
    > = [
      this.getChallengeInsightProgressChallengeTargetSuccessQuery.execute(
        props
      ),
    ];

    if (metricType === AccountMetricInsightsMetricType.SelfReport) {
      promises.push(
        this.getChallengeInsightProgressSelfReportSummaryQuery.execute(props)
      );
    }

    if (metricType === AccountMetricInsightsMetricType.DigitalWorkingHours) {
      promises.push(
        this.getChallengeInsightProgressDigitalWorkingHoursSummaryQuery.execute(
          props
        )
      );
    }

    if (metricType === AccountMetricInsightsMetricType.FocusV1) {
      promises.push(
        this.getChallengeInsightProgressFocusV1SummaryQuery.execute(props)
      );
    }

    if (
      props.params.metricType ===
      AccountMetricInsightsMetricType.DigitalIntensity
    ) {
      promises.push(
        this.getChallengeInsightProgressDigitalIntensitySummaryQuery.execute(
          props
        )
      );
    }

    if (metricType === AccountMetricInsightsMetricType.Sentiment) {
      promises.push(
        this.getChallengeInsightProgressSentimentSummaryQuery.execute(props)
      );
    }
    if (metricType === AccountMetricInsightsMetricType.TimeInMeetings) {
      promises.push(
        this.getChallengeInsightProgressTimeInMeetingsSummaryQuery.execute(
          props
        )
      );
    }

    if (metricType === AccountMetricInsightsMetricType.TimeInCalls) {
      promises.push(
        this.getChallengeInsightProgressTimeInCallsSummaryQuery.execute(props)
      );
    }

    if (metricType === AccountMetricInsightsMetricType.TimeInApp) {
      promises.push(
        this.getChallengeInsightProgressTimeInAppSummaryQuery.execute(props)
      );
    }

    if (metricType === AccountMetricInsightsMetricType.TimeInCategory) {
      promises.push(
        this.getChallengeInsightProgressTimeInCategorySummaryQuery.execute(
          props
        )
      );
    }

    const [successPercentage, entity] = await Promise.all(promises);
    entity.setSuccessPercentage(successPercentage);
    entity.setChallengeTargetOperator(
      props.params.challengeTargetOperator ?? ChallengeTargetOperatorEnum.MAX
    );
    return this.presenter.toResponse(entity);
  }
}
